import {
  Alignment,
  Background,
  Bluex,
  BorderRadius,
  Button,
  Direction,
  Displaying,
  dontForwardTheseProps,
  FontSize,
  Spacing,
} from "@bonusx-global/bx-design-system";
import type { IWithIsMobile } from "@bonusx-global/bx-design-system/dist/types/interfaces";
import { styled } from "@mui/material";
import type { BoxProps } from "@mui/material/Box";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { LOGGED_HOME_PAGE_PATH } from "../../../../constants/env";
import useIsMobile from "../../../../hooks/device/useIsMobile";
import Emoji from "../../../../legacy/components/common/Emoji";
import { Loading } from "../../../../legacy/components/Loading";
import { useProfileMigrationQuery } from "../../../../api/features/profile/useProfileMigration.query";

const TITLE_TEXT = "Stiamo sfornando il tuo nuovo profilo!";
const BODY_TEXT =
  "Abbiamo quasi finito di preparare la tua nuova esperienza. Senti già il profumino? Ancora qualche secondo e sarà pronto!";
const REFRESH_BUTTON_TEXT = "Ricarica";
const CHEF_EMOJI = "🧑‍🍳";

const BODY_GAP_DESKTOP = "8rem";
const BODY_GAP_MOBILE = "1rem";
const BUTTON_MAX_WIDTH = "256px";

const ProfileMigrationContainer = styled(Box, {
  shouldForwardProp: dontForwardTheseProps<IWithIsMobile & IProfileMigrationProps, BoxProps>(["isMobile", "variant"]),
})<IWithIsMobile & IProfileMigrationProps>(({ isMobile, variant }) => ({
  height: "100%",
  paddingLeft: isMobile ? BODY_GAP_MOBILE : BODY_GAP_DESKTOP,
  paddingRight: isMobile ? BODY_GAP_MOBILE : BODY_GAP_DESKTOP,
  alignItems: Alignment.center,
  justifyContent: Alignment.center,
  backgroundColor: variant === "white" ? Background.paper : Background.default,
}));

const ProfileMigrationContent = styled(Box, {
  shouldForwardProp: dontForwardTheseProps<IWithIsMobile, BoxProps>(["isMobile"]),
})<IWithIsMobile>(({ isMobile }) => ({
  height: "100%",
  display: Displaying.flex,
  flexDirection: Direction.column,
  alignItems: Alignment.center,
  justifyContent: isMobile ? Alignment.center : Alignment.start,
  gap: Spacing["2500"],
  textAlign: Alignment.center,
  paddingTop: isMobile ? 0 : Spacing["4000"],
}));

const ProfileMigrationEmojiWrapper = styled(Box)({
  borderRadius: BorderRadius.full,
  backgroundColor: Bluex["50"],
  display: Displaying.flex,
  alignItems: Alignment.center,
  justifyContent: Alignment.center,
  height: Spacing["4000"],
  width: Spacing["4000"],
});

const PROFILE_MIGRATION_REVALIDATE_DELAY_MS = 3500;

interface IProfileMigrationProps {
  variant?: "default" | "white";
}

export function ProfileMigrationPage({ variant = "default" }: Readonly<IProfileMigrationProps>) {
  const navigate = useNavigate();
  const {
    profileMigration,
    loading: profileMigrationLoading,
    revalidate: revalidateProfileMigration,
    error: retrieveProfileMigrationError,
  } = useProfileMigrationQuery();

  const isMobile = useIsMobile();

  function handleRefreshButtonClick() {
    window.location.reload();
  }

  const isMigrationInProgress = profileMigration?.isMigrationInProgress;

  useEffect(
    function revalidateProfileMigrationDuringMigration() {
      if (!isMigrationInProgress) {
        return;
      }
      const intervalId = setInterval(revalidateProfileMigration, PROFILE_MIGRATION_REVALIDATE_DELAY_MS);
      return function cleanup() {
        clearInterval(intervalId);
      };
    },
    [isMigrationInProgress, revalidateProfileMigration]
  );

  useEffect(
    function handleRetrieveProfileMigrationError() {
      if (retrieveProfileMigrationError && !profileMigrationLoading) {
        navigate(LOGGED_HOME_PAGE_PATH);
      }
    },
    [retrieveProfileMigrationError, profileMigrationLoading, navigate]
  );

  const migrationStateNotReady = profileMigrationLoading || profileMigration === undefined;
  if (migrationStateNotReady) {
    return <Loading />;
  }

  const isMigrationComplete = profileMigration?.isMigrationInProgress === false;
  if (isMigrationComplete) {
    return <Navigate replace to={LOGGED_HOME_PAGE_PATH} />;
  }

  return (
    <ProfileMigrationContainer isMobile={isMobile} variant={variant}>
      <ProfileMigrationContent isMobile={isMobile}>
        <Typography variant="h4">{TITLE_TEXT}</Typography>

        <ProfileMigrationEmojiWrapper>
          <Emoji ariaLabel="chef" emojiSymbol={CHEF_EMOJI} fontSize={FontSize["2xl"]} />
        </ProfileMigrationEmojiWrapper>

        <Typography variant="body1" maxWidth={isMobile ? "100%" : "50%"}>
          {BODY_TEXT}
        </Typography>
        <Box width={BUTTON_MAX_WIDTH} paddingTop={isMobile ? 0 : "5rem"}>
          <Button size="large" variant="contained" color="primary" onClick={handleRefreshButtonClick} fullWidth>
            {REFRESH_BUTTON_TEXT}
          </Button>
        </Box>
      </ProfileMigrationContent>
    </ProfileMigrationContainer>
  );
}
